/**
 * Боковой док у края экрана (ШАГ 1 «Карты без сети не закрывают экран»,
 * 23.09): круглая кнопка-ярлык, которую можно перетащить по вертикали и к
 * левому/правому краю (механика — useEdgeDock, порт k24). Открывает лист
 * DockSheet — снизу на телефоне, панелью у края на широком экране. Ничего
 * специфичного про пакеты карт здесь нет: имя нейтральное, компонент годится
 * для любой следующей плавающей кнопки вьюера.
 */
import { tr } from '@app/i18n'
import type { ComponentChildren } from 'preact'
import { useEffect, useRef } from 'preact/hooks'
import { useDetentSwipe } from './use-detent'
import { type DockEdge, useEdgeDock } from './use-edge-dock'

export interface EdgeDockProps {
  /** Ключ localStorage — свой на каждую кнопку дока (см. use-edge-dock.ts). */
  storageKey: string
  ariaLabel: string
  /** aria-hidden содержимое кнопки — svg/эмодзи. */
  icon: ComponentChildren
  /** Точка/цифра поверх кнопки — идёт загрузка или уже что-то сохранено. */
  badge?: ComponentChildren
  open: boolean
  onToggle: () => void
  defaultEdge?: DockEdge
  defaultRatio?: number
  /**
   * Фиксированный сдвиг вниз в ПИКСЕЛЯХ поверх базового отступа — для
   * стопки из нескольких кнопок дока (план 24.09 п.3: на узких экранах
   * ratio-позиция второй кнопки заезжала на рельс этажей (ныне язычок .floor-tab) — доля высоты
   * дорожки зависит от viewport и не даёт гарантированного зазора).
   * Кнопка высотой 48px, зазор между кнопками — 12px.
   */
  stackOffsetPx?: number
  /** Край дока после восстановления/переноса — для DockSheet на широком экране. */
  onEdgeChange?: (edge: DockEdge) => void
}

/**
 * Дорожка короче экрана: кнопка не должна упираться в шапку и низ карты.
 * Экспортирована — та же дорожка нужна другим плавающим докам вьюера
 * (рельс этажей ТЦ, SOS-язычок, 25.09), чтобы не заводить второй расчёт.
 */
export const DOCK_RAIL =
  'calc(100vh - 220px - env(safe-area-inset-top) - env(safe-area-inset-bottom))'
const RAIL = DOCK_RAIL

export function EdgeDock(props: EdgeDockProps) {
  const dock = useEdgeDock({
    storageKey: props.storageKey,
    ...(props.defaultEdge !== undefined ? { defaultEdge: props.defaultEdge } : {}),
    ...(props.defaultRatio !== undefined ? { defaultRatio: props.defaultRatio } : {}),
  })
  const { onEdgeChange } = props
  useEffect(() => {
    onEdgeChange?.(dock.edge)
  }, [dock.edge, onEdgeChange])
  return (
    <button
      type="button"
      class={`edge-dock-btn glass edge-dock-${dock.edge}${props.open ? ' edge-dock-btn-open' : ''}`}
      aria-label={props.ariaLabel}
      aria-expanded={props.open}
      title={`${props.ariaLabel} · ${tr('dock.hint')}`}
      {...dock.handlers}
      style={{
        top: `calc(env(safe-area-inset-top) + ${96 + (props.stackOffsetPx ?? 0)}px + ${dock.ratio} * (${RAIL}))`,
        touchAction: 'none',
      }}
      onClick={() => {
        // Перенос не должен считаться нажатием — иначе палец, отпущенный
        // после драга, случайно открыл бы лист.
        if (dock.justDragged()) return
        props.onToggle()
      }}
    >
      <span aria-hidden="true" class="edge-dock-icon">
        {props.icon}
      </span>
      {props.badge && (
        <span class="edge-dock-badge" aria-hidden="true">
          {props.badge}
        </span>
      )}
    </button>
  )
}

export interface DockSheetProps {
  title: string
  onClose: () => void
  children: ComponentChildren
  /** На широком экране лист встаёт у того же края, что и кнопка (по умолчанию — правый). */
  edge?: DockEdge
}

/** Лист над доком: снизу на телефоне (CSS media), закрывается свайпом/Esc/крестиком. */
export function DockSheet(props: DockSheetProps) {
  const { onClose, edge = 'right' } = props
  const ref = useRef<HTMLDivElement | null>(null)
  const swipe = useDetentSwipe(
    () => undefined,
    () => onClose(),
  )

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    ref.current?.focus()
  }, [])

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: клик по фону — вторичный жест закрытия; клавиатура — Esc и кнопка ✕
    <div class={`edge-dock-scrim edge-dock-scrim-${edge}`} onClick={onClose}>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation, не действие */}
      <section
        ref={ref}
        class="edge-dock-sheet glass anim-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div class="edge-dock-handle" onPointerDown={swipe.onPointerDown} aria-hidden="true" />
        <header class="edge-dock-head">
          <p class="edge-dock-title">{props.title}</p>
          <button type="button" class="icon-btn" aria-label={tr('common.close')} onClick={onClose}>
            ✕
          </button>
        </header>
        <div class="edge-dock-body">{props.children}</div>
      </section>
    </div>
  )
}
